'use client';

import dynamic from 'next/dynamic';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { BusinessProvider } from '@/lib/business-context';
import { AuthModalProvider } from '@/components/auth/auth-modal';
import { Toaster } from 'sonner';
import { useStorefrontCart } from '@/lib/storefront-cart-context';
const AppCartSidePanel = dynamic(() => import('@/components/storefront/app-cart-side-panel').then(module => module.AppCartSidePanel));
import { AppTopBar } from '@/components/layout/app-top-bar';
import { BottomNav } from '@/components/layout/bottom-nav';
import { Sidebar } from '@/components/layout/sidebar';
import { AddPostModalProvider } from '@/components/marketplace/add-post-modal';
import { FeatureFlagProvider } from '@/lib/feature-flags';
import { PullToRefresh } from '@/components/native/pull-to-refresh';
import { PageTransition } from '@/components/native/page-transition';
const SpotPanel = dynamic(() => import('@/components/assistant/spot-panel').then(module => module.SpotPanel));
import { PushProvider } from '@/components/native/push-provider';
import { UpgradeWallProvider } from '@/components/billing/upgrade-wall-provider';

const SPOT_OPEN_KEY = 'sweetspot_spot_open';

function AppShell({ children }: { children: React.ReactNode }) {
  const { items } = useStorefrontCart();
  const [cartOpen, setCartOpen] = useState(false);
  const [cartMounted, setCartMounted] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Spot is a companion on desktop: the rail stays open across navigations
  // and across reloads. Phones always start closed — a sheet that pops up
  // on launch is the wrong first impression.
  const [spotOpen, setSpotOpen] = useState(false);
  const [spotMounted, setSpotMounted] = useState(false);
  useEffect(() => {
    try {
      if (window.matchMedia('(min-width: 768px)').matches && localStorage.getItem(SPOT_OPEN_KEY) === '1') {
        setSpotMounted(true);
        setSpotOpen(true);
      }
    } catch { /* storage unavailable — start closed */ }
  }, []);
  const setSpot = (open: boolean) => {
    if (open) setSpotMounted(true);
    setSpotOpen(open);
    try { localStorage.setItem(SPOT_OPEN_KEY, open ? '1' : '0'); } catch { /* ignore */ }
  };

  const cartCount = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden overscroll-none">
      <Suspense fallback={<div className="flex-none h-16 border-b bg-background md:h-14" />}>
        <AppTopBar
          cartCount={cartCount}
          onCartClick={() => { setCartMounted(true); setCartOpen((prev) => !prev); }}
          showNotifications
          onSpotClick={() => setSpot(!spotOpen)}
          spotOpen={spotOpen}
          className="fixed inset-x-0 top-0 md:static"
        />
      </Suspense>
      <div className="flex-1 min-h-0 flex">
        <Sidebar mobileOpen={sidebarOpen} onMobileOpenChange={setSidebarOpen} />
        <PullToRefresh className="app-main flex-1 min-w-0 overflow-y-auto overscroll-y-contain flex flex-col">
          <PageTransition>{children}</PageTransition>
        </PullToRefresh>
        {/* Always mounted so the conversation survives closing the panel and
            moving between pages: a docked rail on desktop, a sheet on phones. */}
        {spotMounted && <SpotPanel open={spotOpen} onClose={() => setSpot(false)} />}
      </div>
      <BottomNav onMoreClick={() => setSidebarOpen((prev) => !prev)} />
      {cartMounted && <AppCartSidePanel open={cartOpen} onOpenChange={setCartOpen} />}
      {/* Inside AppShell so it sits under BusinessProvider — the resolved
          business is what tells it the visitor is signed in. */}
      <PushProvider />
    </div>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <BusinessProvider>
      <FeatureFlagProvider>
        <AuthModalProvider>
          <AddPostModalProvider>
            <UpgradeWallProvider>
              <AppShell>{children}</AppShell>
            </UpgradeWallProvider>
          </AddPostModalProvider>
          <Toaster
            position="bottom-right"
            richColors
            closeButton
            // Sit above the bottom tab bar + home indicator on phones.
            mobileOffset={{ bottom: 'calc(var(--app-bottom-inset) + 4px)' }}
          />
        </AuthModalProvider>
      </FeatureFlagProvider>
    </BusinessProvider>
  );
}
